<template>
    <div class="voltage-rise-inverter">
        <div class="voltage-rise-calculator__header">
            <div>
                <h3 class="h4">
                    {{ preExisting ? 'Pre-existing inverter' : 'Inverter' }}
                    {{ preExisting ? distribution.node - inverters : distribution.node }}
                </h3>
                <span class="voltage-rise-calculator__tick small semi" :class="{'is-completed' : isWithinLimit}">
                    {{ isWithinLimit ? 'Within limit' : 'Above limit' }}
                    <figure>
                        <svg v-if="isWithinLimit" class="icon">
                            <use xlink:href="#tick" />
                        </svg>
                        <svg v-else class="icon">
                            <use xlink:href="#close" />
                        </svg>
                    </figure>
                </span>
            </div>
        </div>

        <ul
            v-for="data in voltageRiseData"
            :key="data.phase"
            class="voltage-rise-inverter__voltage"
            :class="{'is-phase2' : data.phase === 1, 'is-phase3' : data.phase === 2}"
        >
            <li>
                <strong>{{ data.percent.toFixed(2) }}</strong>
                <small class="small semi">Phase {{ data.phase + 1 }} volt rise (%)</small>
            </li>
            <li>
                <strong>{{ data.volt.toFixed(2) }}</strong>
                <small class="small semi">Phase {{ data.phase + 1 }} volt rise (V)</small>
            </li>
        </ul>

        <ul class="voltage-rise-inverter__group">
            <li class="voltage-rise-inverter__item">
                <h6 class="small semi">
                    <span>Permissable volt rise</span>
                    <span class="text--grey-light">%</span>
                </h6>
                <strong class="small semi">{{ permissibleVoltageRisePercent }}</strong>
            </li>
            <li class="voltage-rise-inverter__item">
                <h6 class="small semi">
                    <span>Permissable volt rise</span>
                    <span class="text--grey-light">V</span>
                </h6>
                <strong class="small semi">{{ permissibleVoltageRiseV.toFixed(2) }}</strong>
            </li>
        </ul>

        <ul class="voltage-rise-inverter__group">
            <li class="voltage-rise-inverter__item">
                <h6 class="small semi">
                    <span>Inverter</span>
                    <span class="text--grey-light">VA</span>
                </h6>
                <strong class="small semi">{{ power }}</strong>
            </li>
            <li class="voltage-rise-inverter__item">
                <h6 class="small semi">
                    <span>Nominal single phase voltage</span>
                    <span class="text--grey-light">V</span>
                </h6>
                <strong class="small semi">{{ singlePhaseVoltage }}</strong>
            </li>
            <li class="voltage-rise-inverter__item">
                <h6 class="small semi">
                    <span>Number of phases</span>
                </h6>
                <strong class="small semi">{{ phases }}</strong>
            </li>
            <li class="voltage-rise-inverter__item">
                <h6 class="small semi">
                    <span>AC amps per phase</span>
                    <span class="text--grey-light">V</span>
                </h6>
                <strong class="small semi">{{ ampsPerPhase.toFixed(2) }}</strong>
            </li>
        </ul>

        <ul v-if="!preExisting && limitLocation" class="voltage-rise-inverter__group">
            <li class="voltage-rise-inverter__item">
                <h6 class="small semi">
                    <span>Export limitation device</span>
                </h6>
                <strong class="small semi">{{ limitLocation }}</strong>
            </li>
            <li class="voltage-rise-inverter__item">
                <h6 class="small semi">
                    <span>Limiting amount</span>
                    <span class="text--grey-light">W</span>
                </h6>
                <strong class="small semi">{{ limit }}</strong>
            </li>
        </ul>
    </div>
</template>

<script>
/* eslint-disable no-console */
export default {
    props: {
        distribution: {
            type: Object,
            required: true,
        },
        supply: {
            type: Array,
            required: true,
        },
        system: {
            type: Array,
            required: true,
        },
        singlePhaseVoltage: {
            type: [Number,String],
            required: true,
        },
        totalWithinLimit: {
            type: Boolean,
            default: false
        },
        inverters: {
            type: Number,
            required: true
        }
    },


    data() {
        return {
            permissablePercent: .02,
            cableData: {
                1: [45,	90],
                1.5: [70, 140],
                2.5: [128, 256],
                4: [205, 412],
                6: [306, 615],
                10: [515, 1034],
                16: [818, 1643],
                25: [1289, 2588],
                35: [1773, 3560],
                50: [2377, 4772],
                70: [3342, 6712],
                95: [4445, 8927],
            },
        };
    },

    computed: {
        power() {
            return this.preExisting ? this.distribution.cable.powerOutput : this.distribution.destination.powerOutput ;
        },

        phase() {
            if (this.distribution.destination.phases == 3) return this.distribution.destination.phases
            return this.distribution.cable.phases;
        },

        phases() {
            return this.distribution.destination.phases;
        },

        limitLocation() {
            return this.distribution.limitationDevice ? this.distribution.limitationDevice.location : null
        },

        limit() {
            return this.distribution.limitationDevice ? eval(this.distribution.limitationDevice.limit) : null
        },

        ampsPerPhase() {
            if (this.phases == 3) {
                return this.power / eval(this.singlePhaseVoltage) / 3
            }
            return this.power / eval(this.singlePhaseVoltage);
        },

        preExisting() {
            return this.distribution.isPreExistingSystem == 'true' || this.distribution.isPreExistingSystem === true;
        },

        getSegments() {
            let total = [];

            this.supply.forEach((supply, index) => {
                const segments = this.system
                    .filter(system => this.phases == 3 || system.destination.phases == 3 || this.phase == system.cable.phases)
                    .filter(system => {
                        const nextNode = this.supply[index+1];
                        if (String(supply.isDistributionBoard) == 'true' && system.connected !== supply.node) return false
                        if ((!nextNode || String(nextNode.isDistributionBoard) == 'true') && supply.node == this.distribution.connected) return true
                        if (nextNode && supply.node == nextNode.connected) return true
                        return false;
                    })
                    .map(system => {
                        return {
                            ...supply,
                            limitationDevice: system.limitationDevice,
                            cable: {
                                ...supply.cable,
                                phases: system.cable.phases,
                                power: system.cable.powerOutput || system.destination.powerOutput
                            }
                        }
                    })

                total = total.concat(segments);
            })

            total.push({
                ...this.distribution,
                cable: {
                    ...this.distribution.cable,
                    power: this.distribution.cable.powerOutput || this.distribution.destination.powerOutput
                }
            });

            return total
        },

        permissibleVoltageRiseV() {
            return this.permissibleVoltageRisePercent * eval(this.singlePhaseVoltage) * .01;
        },

        permissibleVoltageRisePercent() {
            return this.permissablePercent * 100;
        },

        voltageRiseV() {
            const voltageArray = [0,0,0];

            this.voltageRisePercent.forEach((voltage, index) => {
                const voltageRise = voltage * eval(this.singlePhaseVoltage) / 100;
                voltageArray[index] = voltageRise;
            });

            return voltageArray;
        },

        voltageRisePercent() {
            const voltageArray = [0,0,0];
            let limited = false;

            this.getSegments.forEach((segment) => {
                let cablePhase = 1;
                if (segment.cable.phases == '0,1,2') cablePhase = 3;

                const cableData = this.getCableData(Number(segment.cable.size));

                let current = Number(segment.cable.power) / eval(this.singlePhaseVoltage) / cablePhase;

                // Determine if the current has been limited or not
                if (
                    segment.limitationDevice
                    && segment.limitationDevice.location
                    && segment.limitationDevice.limit
                    && !limited
                ) {
                    if (segment.limitationDevice.location == segment.destination.label) {
                        limited = true;
                    }

                    current = Number(segment.limitationDevice.limit) / eval(this.singlePhaseVoltage) / cablePhase;
                }

                const voltageRise = current * Number(segment.cable.length) / cableData;

                if (cablePhase == 3 && this.phases == 3) {
                    voltageArray[0] = voltageArray[0] + voltageRise;
                    voltageArray[1] = voltageArray[1] + voltageRise;
                    voltageArray[2] = voltageArray[2] + voltageRise;
                } else {
                    cablePhase = segment.cable.phases

                    // This is a two phase supply. Only apply to the phase
                    // that the inverter is connected to.
                    if (cablePhase.includes(',')) {
                        cablePhase = this.phase;
                    }

                    voltageArray[Number(cablePhase)] = voltageArray[Number(cablePhase)] + voltageRise;
                }
            })

            return voltageArray;
        },

        voltageRiseData() {
            const data = [];
            this.voltageRiseV.map((value, index) => {
                if (value !== 0) {
                    data.push({
                        phase: index,
                        volt: value,
                        percent: this.voltageRisePercent[index],
                    })
                }
            });

            return data;
        },

        isWithinLimit() {
            return this.voltageRiseV.every(value => (value <= this.permissibleVoltageRiseV)) && this.voltageRisePercent.every(value => (value <= this.permissibleVoltageRisePercent));
        },
    },

    created() {
        this.$emit('validate', this.isWithinLimit);
    },

    mounted() {
        this.$emit('updatedata', this.voltageRiseData);
    },

    methods: {
        getCableData(size) {
            // Converted to single phase voltage in the amps per phase
            // if (phases == 3) {
            //     return this.cableData[size][1];
            // }

            return this.cableData[size][0];
        },
    }
};
</script>
